import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Headphones, ArrowRight, Music, Search } from "lucide-react";

const WPM_RANGES = [
  { k: "all", label: "All speeds" },
  { k: "slow", label: "≤ 70 WPM", test: (w) => w <= 70 },
  { k: "mid", label: "71–100 WPM", test: (w) => w > 70 && w <= 100 },
  { k: "fast", label: "> 100 WPM", test: (w) => w > 100 },
];

const COPY = {
  en: {
    module: "Module 01",
    heading: "Audio Dictation",
    intro: "Pick a dictation, put on your headphones and transcribe. We check every word.",
    searchPh: "Search dictations by title…",
    empty: "No dictations available yet.",
    emptyFiltered: "No dictations match your filters.",
    practice: "Practice",
    detailRoute: "/dictation",
  },
  hi: {
    module: "मॉड्यूल 05",
    heading: "आशु लिपिक · Hindi Shorthand",
    intro: "हिंदी श्रुतलेख सुनें और उसे टाइप करके अपनी सटीकता जाँचें।",
    searchPh: "शीर्षक से खोजें…",
    empty: "अभी कोई हिंदी श्रुतलेख उपलब्ध नहीं है।",
    emptyFiltered: "आपके फ़िल्टर से कोई मेल नहीं मिला।",
    practice: "अभ्यास",
    detailRoute: "/hindi-dictation",
  },
};

export default function DictationList({ language = "en" }) {
  const copy = COPY[language] || COPY.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");

  useEffect(() => {
    api.get(`/dictations?language=${language}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [language]);

  const filtered = useMemo(() => {
    const rng = WPM_RANGES.find((r) => r.k === range);
    const query = q.trim().toLowerCase();
    return items.filter((d) => {
      if (rng?.test && !rng.test(d.wpm)) return false;
      if (query && !d.title.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, q, range]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">{copy.module}</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" lang={language}>
            <Headphones className="w-10 h-10" /> {copy.heading}
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl" lang={language}>
            {copy.intro}
          </p>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={copy.searchPh}
              className="rounded-lg h-11 pl-9 font-mono border-border"
              data-testid="dictation-search"
              lang={language}
            />
          </div>
          <div className="inline-flex border border-border" role="tablist">
            {WPM_RANGES.map((r, i) => (
              <button
                key={r.k}
                onClick={() => setRange(r.k)}
                data-testid={`dictation-range-${r.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  range === r.k ? "bg-brand-gold text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground" data-testid="dictations-empty">
            {items.length === 0 ? copy.empty : copy.emptyFiltered}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="dictations-list">
            {filtered.map((d) => (
              <Link
                key={d.id}
                to={`${copy.detailRoute}/${d.id}`}
                data-testid={`dictation-item-${d.id}`}
                className="group border border-border bg-white p-6 flex flex-col transition-all hover:border-brand-gold/40 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground/70">{d.audio_url ? "Ready" : "No audio yet"}</div>
                    <h3 className="text-xl font-black tracking-tight mt-1" lang={language}>{d.title}</h3>
                  </div>
                  <span className="text-xs font-mono px-2 py-1 border border-brand-gold/40 text-brand-gold">{d.wpm} WPM</span>
                </div>
                <div className={`text-sm text-muted-foreground line-clamp-3 flex-1 ${language === "hi" ? "" : "font-mono"}`} lang={language}>{d.text}</div>
                <div className="flex items-center justify-between mt-6">
                  <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    <Music className="w-3.5 h-3.5 inline mr-1" /> {d.word_count} words
                  </span>
                  <span className="text-xs uppercase tracking-widest font-bold text-brand-gold group-hover:underline underline-offset-4">
                    {copy.practice} <ArrowRight className="w-3.5 h-3.5 inline" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
